import type {NumberLike} from "@cm/template-nodes"
import type {MattressCurveLikeNode} from "#mattress/role-markers"
import {MATTRESS_BASE_MESH} from "#mattress/validation"
import type {MattressComponentGraphNode} from "#mattress/nodes/mattress-nodes"
import {createNumberInput, createNumberExport, createMathOperator, createStringInput, createTemplateInput} from "#mattress/assets-definitions/common"
import {
    createMattressCurveInput,
    createMattressComponentGraph,
    createMattressComponnentGraphGenerator,
    createMattressComponentInstance,
    createMattressGroup,
} from "#mattress/assets-definitions/create-mattress"

// NOTE: valid count assumed to be always >= 0
// -1 for min / max count interpreted as no min / max limit
function getComponentGraphNodesForClippedCount(countNode: NumberLike, maxValidCount?: NumberLike, minValidCount?: NumberLike) {
    if (!maxValidCount && !minValidCount) throw new Error("At least one of maxValidCount or minValidCount must be provided")

    const nodes: MattressComponentGraphNode[] = []

    let effectiveCount: NumberLike = countNode
    if (maxValidCount) {
        const maxValidCountSet = createMathOperator("Max Valid Count Set", "ne", maxValidCount, -1)
        const maxValidCountNotSet = createMathOperator("Max Valid Count Not Set", "eq", maxValidCount, -1)

        const clippedCount = createMathOperator("Clipped Count (Max)", "min", effectiveCount, maxValidCount)
        const weightedClippedCount = createMathOperator("Weighted Clipped Count (Max)", "mul", clippedCount, maxValidCountSet)
        const weightedNonClippedCount = createMathOperator("Weighted Non-Clipped Count (Max)", "mul", effectiveCount, maxValidCountNotSet)

        effectiveCount = createMathOperator("Effective Count After Max Clip", "add", weightedClippedCount, weightedNonClippedCount)

        nodes.push(maxValidCountSet, maxValidCountNotSet, clippedCount, weightedClippedCount, weightedNonClippedCount, effectiveCount)
    }

    if (minValidCount) {
        const minValidCountSet = createMathOperator("Min Valid Count Set", "ne", minValidCount, -1)
        const minValidCountNotSet = createMathOperator("Min Valid Count Not Set", "eq", minValidCount, -1)

        const clippedCount = createMathOperator("Clipped Count (Min)", "max", effectiveCount, minValidCount)
        const weightedClippedCount = createMathOperator("Weighted Clipped Count (Min)", "mul", clippedCount, minValidCountSet)
        const weightedNonClippedCount = createMathOperator("Weighted Non-Clipped Count (Min)", "mul", effectiveCount, minValidCountNotSet)

        effectiveCount = createMathOperator("Effective Count After Min Clip", "add", weightedClippedCount, weightedNonClippedCount)

        nodes.push(minValidCountSet, minValidCountNotSet, clippedCount, weightedClippedCount, weightedNonClippedCount, effectiveCount)
    }

    return {
        nodes,
        clippedCount: effectiveCount,
    }
}

export function getComponentGraphNodesForVerticalOrHorizontalHandlesStackElement() {
    const offsetInput = createNumberInput("Offset", "offset", 0)
    const spacingInput = createNumberInput("Spacing", "spacing", 10)
    const offsetNext = createMathOperator("Offset Next", "add", offsetInput, spacingInput)
    const offsetNextExport = createNumberExport("Offset Next", "offset-next", offsetNext)

    const handleGraphInput = createTemplateInput("Handle", "handle")
    const handleInstance = createMattressComponentInstance("Handle", "handle", handleGraphInput, {
        offset: offsetInput,
        "base-mesh": MATTRESS_BASE_MESH,
    })

    return {
        nodes: [offsetInput, spacingInput, offsetNext, offsetNextExport, handleGraphInput, handleInstance],
        exports: {
            offsetNext: offsetNextExport,
        },
    }
}

export function getComponentGraphNodesForVerticalHandlesStack() {
    const bottomCurve = createMattressCurveInput("Bottom Curve", "bottom-curve")
    const topCurve = createMattressCurveInput("Top Curve", "top-curve")
    const spacing = createNumberInput("Spacing", "spacing", 10)
    const side = createStringInput("Side", "side", "right")
    const handlesCount = createNumberInput("Handles Count", "handles-count", 0)
    const maxHandlesCount = createNumberInput("Max Handles Count", "max-handles-count", -1)

    const {nodes: clippedHandlesCountNodes, clippedCount: effectiveHandlesCount} = getComponentGraphNodesForClippedCount(handlesCount, maxHandlesCount)
    const handlesCountGroup = createMattressGroup("Handles Count", [handlesCount, maxHandlesCount, ...clippedHandlesCountNodes])

    const handlesCountMinusOne = createMathOperator("Handles Count Minus One", "add", effectiveHandlesCount, -1)
    const handlesStackSize = createMathOperator("Handles Stack Size", "mul", handlesCountMinusOne, spacing)
    const handlesStackStartOffset = createMathOperator("Handles Stack Start Offset", "mul", handlesStackSize, -0.5)

    const handlesStackGraphBaseSpacingInput = createNumberInput("Spacing", "spacing", 10)
    const handlesStackGraphBaseBottomCurveInput = createMattressCurveInput("Bottom Curve", "bottom-curve")
    const handlesStackGraphBaseTopCurveInput = createMattressCurveInput("Top Curve", "top-curve")
    const handlesStackGraphBaseSideInput = createStringInput("Side", "side", "right")
    const handlesStackGraphBaseHandleGraphInput = createTemplateInput("Handle Graph", "handle")
    const handlesStackGraphBase = createMattressComponentGraph("Handles Stack Base", [
        handlesStackGraphBaseSpacingInput,
        handlesStackGraphBaseBottomCurveInput,
        handlesStackGraphBaseTopCurveInput,
        handlesStackGraphBaseHandleGraphInput,
        handlesStackGraphBaseSideInput,
    ])

    const handleGraph = createTemplateInput("Handle Graph", "handle")

    const {nodes: handlesStackElementNodes, exports: handlesStackElementExports} = getComponentGraphNodesForVerticalOrHorizontalHandlesStackElement()
    const handlesStackElement = createMattressComponentGraph("Handles Stack Element", handlesStackElementNodes)
    const handlesStackGraph = createMattressComponnentGraphGenerator("Handles Stack", {
        type: "MattressComponentGraphGeneratorUnitRepeat1D",
        unitGraph: handlesStackElement,
        baseGraph: handlesStackGraphBase,
        unitInputs: {
            spacing: handlesStackGraphBaseSpacingInput,
            offset: handlesStackElementExports.offsetNext,
            handle: handlesStackGraphBaseHandleGraphInput,
            "handle/bottom-curve": handlesStackGraphBaseBottomCurveInput,
            "handle/top-curve": handlesStackGraphBaseTopCurveInput,
            "handle/side": handlesStackGraphBaseSideInput,
        },
        unitIdPrefix: "handle",
        unitCount: effectiveHandlesCount,
    })
    const handlesStack = createMattressComponentInstance("Handles Stack", "handles-stack", handlesStackGraph, {
        "handle-0/offset": handlesStackStartOffset,
        spacing: spacing,
        "top-curve": topCurve,
        "bottom-curve": bottomCurve,
        handle: handleGraph,
        side: side,
    })

    return {
        nodes: [
            bottomCurve,
            topCurve,
            spacing,
            side,
            handlesCountGroup,
            handlesCountMinusOne,
            handlesStackSize,
            handlesStackStartOffset,
            handleGraph,
            handlesStackGraphBase,
            handlesStackGraph,
            handlesStack,
        ],
        stackInstance: handlesStack,
    }
}

export function getComponentGraphNodesForHorizontalHandlesStack() {
    const refCurve = createMattressCurveInput("Ref Curve", "ref-curve")
    const spacing = createNumberInput("Spacing", "spacing", 10)
    const side = createStringInput("Side", "side", "right")

    const handlesCount = createNumberInput("Handles Count", "handles-count", 0)
    const maxHandlesCount = createNumberInput("Max Handles Count", "max-handles-count", -1)

    const {nodes: clippedHandlesCountNodes, clippedCount: effectiveHandlesCount} = getComponentGraphNodesForClippedCount(handlesCount, maxHandlesCount)
    const handlesCountGroup = createMattressGroup("Handles Count", [handlesCount, maxHandlesCount, ...clippedHandlesCountNodes])

    const handlesCountMinusOne = createMathOperator("Handles Count Minus One", "add", effectiveHandlesCount, -1)
    const handlesStackSize = createMathOperator("Handles Stack Size", "mul", handlesCountMinusOne, spacing)
    const handlesStackStartOffset = createMathOperator("Handles Stack Start Offset", "mul", handlesStackSize, -0.5)

    const handlesStackGraphBaseSpacingInput = createNumberInput("Spacing", "spacing", 10)
    const handlesStackGraphBaseRefCurveInput = createMattressCurveInput("Ref Curve", "ref-curve")
    const handlesStackGraphBaseSideInput = createStringInput("Side", "side", "right")
    const handlesStackGraphBaseHandleGraphInput = createTemplateInput("Handle Graph", "handle")
    const handlesStackGraphBase = createMattressComponentGraph("Handles Stack Base", [
        handlesStackGraphBaseSpacingInput,
        handlesStackGraphBaseRefCurveInput,
        handlesStackGraphBaseSideInput,
        handlesStackGraphBaseHandleGraphInput,
    ])

    const handleGraph = createTemplateInput("Handle Graph", "handle")

    const {nodes: handlesStackElementNodes, exports: handlesStackElementExports} = getComponentGraphNodesForVerticalOrHorizontalHandlesStackElement()
    const handlesStackElement = createMattressComponentGraph("Handles Stack Element", handlesStackElementNodes)

    const handlesStackGraph = createMattressComponnentGraphGenerator("Handles Stack", {
        type: "MattressComponentGraphGeneratorUnitRepeat1D",
        unitGraph: handlesStackElement,
        baseGraph: handlesStackGraphBase,
        unitInputs: {
            spacing: handlesStackGraphBaseSpacingInput,
            offset: handlesStackElementExports.offsetNext,
            handle: handlesStackGraphBaseHandleGraphInput,
            "handle/ref-curve": handlesStackGraphBaseRefCurveInput,
            "handle/side": handlesStackGraphBaseSideInput,
        },
        unitIdPrefix: "handle",
        unitCount: effectiveHandlesCount,
    })

    const handlesStack = createMattressComponentInstance("Handles Stack", "handles-stack", handlesStackGraph, {
        "handle-0/offset": handlesStackStartOffset,
        spacing: spacing,
        "ref-curve": refCurve,
        handle: handleGraph,
        side: side,
    })

    return {
        nodes: [
            refCurve,
            spacing,
            side,
            handlesCountGroup,
            handlesCountMinusOne,
            handlesStackSize,
            handlesStackStartOffset,
            handleGraph,
            handlesStackGraphBase,
            handlesStackGraph,
            handlesStack,
        ],
        stackInstance: handlesStack,
    }
}

export function getComponentGraphNodesForHandles(inputData: {
    bottomHeightCurve: MattressCurveLikeNode
    middlePartTopCurve: MattressCurveLikeNode
    middlePartTopLayerMiddleCurve: MattressCurveLikeNode
    mattressWidth: NumberLike
    middlePartFabricLayersCount: NumberLike
}) {
    const {bottomHeightCurve, middlePartTopCurve, middlePartTopLayerMiddleCurve, mattressWidth} = inputData

    // max count control logic
    const fabricLayerCountGtZero = createMathOperator("Fabric Layer Count Greater Than Zero", "gt", inputData.middlePartFabricLayersCount, 0)

    // front / back
    const widthThreshold = 120
    const widthAboveThreshold = createMathOperator("Width Above Threshold", "gt", mattressWidth, widthThreshold)
    const widthBelowThreshold = createMathOperator("Width Below Threshold", "lte", mattressWidth, widthThreshold)
    const maxCountIfWidthAboveThreshold = createMathOperator("Max Count If Width Above Threshold", "mul", widthAboveThreshold, 2)
    const maxCountIfWidthBelowThreshold = createMathOperator("Max Count If Width Below Threshold", "mul", widthBelowThreshold, 1)
    const frontAndBackMaxHandlesCountTmp = createMathOperator(
        "Front and Back Max Handles Count Tmp",
        "add",
        maxCountIfWidthAboveThreshold,
        maxCountIfWidthBelowThreshold,
    )
    const frontAndBackMaxHandlesCount = createMathOperator("Front and Back Max Handles Count", "mul", frontAndBackMaxHandlesCountTmp, fabricLayerCountGtZero)

    // left / right
    const leftAndRightMaxHandlesCount = createMathOperator("Left and Right Max Handles Count", "mul", -1, fabricLayerCountGtZero)

    const handlesCountLogicGroup = createMattressGroup("Handles Count Logic", [
        fabricLayerCountGtZero,
        widthAboveThreshold,
        widthBelowThreshold,
        maxCountIfWidthAboveThreshold,
        maxCountIfWidthBelowThreshold,
        frontAndBackMaxHandlesCountTmp,
        frontAndBackMaxHandlesCount,
        leftAndRightMaxHandlesCount,
    ])

    // vertical handles stacks
    const verticalHandleGraphInput = createTemplateInput("Vertical Handle Graph", "vertical-handle")

    const {nodes: verticalHandlesStackNodes} = getComponentGraphNodesForVerticalHandlesStack()
    const verticalHandlesStackGraph = createMattressComponentGraph("Vertical Handles Stack Graph", verticalHandlesStackNodes)

    const frontSideVerticalHandlesStack = createMattressComponentInstance(
        "Front Side Vertical Handles Stack",
        "front-side-vertical-handles-stack",
        verticalHandlesStackGraph,
        {
            "bottom-curve": bottomHeightCurve,
            "top-curve": middlePartTopCurve,
            side: "front",
            handle: verticalHandleGraphInput,
            "max-handles-count": frontAndBackMaxHandlesCount,
        },
    )
    const backSideVerticalHandlesStack = createMattressComponentInstance(
        "Back Side Vertical Handles Stack",
        "back-side-vertical-handles-stack",
        verticalHandlesStackGraph,
        {
            "bottom-curve": bottomHeightCurve,
            "top-curve": middlePartTopCurve,
            side: "back",
            handle: verticalHandleGraphInput,
            "max-handles-count": frontAndBackMaxHandlesCount,
        },
    )
    const leftSideVerticalHandlesStack = createMattressComponentInstance(
        "Left Side Vertical Handles Stack",
        "left-side-vertical-handles-stack",
        verticalHandlesStackGraph,
        {
            "bottom-curve": bottomHeightCurve,
            "top-curve": middlePartTopCurve,
            side: "left",
            handle: verticalHandleGraphInput,
            "max-handles-count": leftAndRightMaxHandlesCount,
        },
    )
    const rightSideVerticalHandlesStack = createMattressComponentInstance(
        "Right Side Vertical Handles Stack",
        "right-side-vertical-handles-stack",
        verticalHandlesStackGraph,
        {
            "bottom-curve": bottomHeightCurve,
            "top-curve": middlePartTopCurve,
            side: "right",
            handle: verticalHandleGraphInput,
            "max-handles-count": leftAndRightMaxHandlesCount,
        },
    )

    // horizontal handles stacks
    const horizontalHandleGraphInput = createTemplateInput("Horizontal Handle Graph", "horizontal-handle")

    const {nodes: horizontalHandlesStackNodes} = getComponentGraphNodesForHorizontalHandlesStack()
    const horizontalHandlesStackGraph = createMattressComponentGraph("Horizontal Handles Stack Graph", horizontalHandlesStackNodes)

    const frontSideHorizontalHandlesStack = createMattressComponentInstance(
        "Front Side Horizontal Handles Stack",
        "front-side-horizontal-handles-stack",
        horizontalHandlesStackGraph,
        {
            "ref-curve": middlePartTopLayerMiddleCurve,
            side: "front",
            handle: horizontalHandleGraphInput,
            "max-handles-count": frontAndBackMaxHandlesCount,
        },
    )
    const backSideHorizontalHandlesStack = createMattressComponentInstance(
        "Back Side Horizontal Handles Stack",
        "back-side-horizontal-handles-stack",
        horizontalHandlesStackGraph,
        {
            "ref-curve": middlePartTopLayerMiddleCurve,
            side: "back",
            handle: horizontalHandleGraphInput,
            "max-handles-count": frontAndBackMaxHandlesCount,
        },
    )
    const leftSideHorizontalHandlesStack = createMattressComponentInstance(
        "Left Side Horizontal Handles Stack",
        "left-side-horizontal-handles-stack",
        horizontalHandlesStackGraph,
        {
            "ref-curve": middlePartTopLayerMiddleCurve,
            side: "left",
            handle: horizontalHandleGraphInput,
            "max-handles-count": -1,
        },
    )
    const rightSideHorizontalHandlesStack = createMattressComponentInstance(
        "Right Side Horizontal Handles Stack",
        "right-side-horizontal-handles-stack",
        horizontalHandlesStackGraph,
        {
            "ref-curve": middlePartTopLayerMiddleCurve,
            side: "right",
            handle: horizontalHandleGraphInput,
            "max-handles-count": -1,
        },
    )

    return {
        nodes: [
            handlesCountLogicGroup,
            verticalHandleGraphInput,
            verticalHandlesStackGraph,
            frontSideVerticalHandlesStack,
            backSideVerticalHandlesStack,
            leftSideVerticalHandlesStack,
            rightSideVerticalHandlesStack,
            horizontalHandleGraphInput,
            horizontalHandlesStackGraph,
            frontSideHorizontalHandlesStack,
            backSideHorizontalHandlesStack,
            leftSideHorizontalHandlesStack,
            rightSideHorizontalHandlesStack,
        ],
    }
}
